import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Linkedin, Facebook, Instagram, Mail, Phone, MapPin, ArrowRight, Sparkles } from "lucide-react";
import axios from "axios";
import { motion } from "framer-motion";
import { API } from "@/App";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { siteContact } from "@/config/site";

const LOGO_SRC = "/assets/Free__2_-removebg-preview.png";

const FOOTER_NAV = {
  Company: [
    { label: "About Us",      to: "/about" },
    { label: "Our Work",      to: "/our-work" },
    { label: "Case Studies",  to: "/case-studies" },
    { label: "Our Clients",   to: "/our-clients" },
    { label: "Contact Us",    to: "/contact" },
  ],
  Legal: [
    { label: "Terms & Conditions", to: "/terms" },
    { label: "Privacy Policy",     to: "/privacy" },
    { label: "Cookie Policy",      to: "/cookies" },
  ],
};

const SOCIAL = [
  { label: "LinkedIn",  href: "https://www.linkedin.com/", Icon: Linkedin },
  { label: "Instagram", href: "https://www.instagram.com/namespaceconsultants/", Icon: Instagram },
  { label: "Facebook",  href: "https://www.facebook.com/", Icon: Facebook },
];

const Footer = () => {
  const [newsletter, setNewsletter] = useState({ email: "", subscribe: false, website: "" });
  const [loading, setLoading] = useState(false);
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletter.email.trim()) return toast.error("Email is required");
    if (!emailRegex.test(newsletter.email)) return toast.error("Please enter a valid email address");
    try {
      setLoading(true);
      await axios.post(`${API}/contact/send`, {
        name: "Newsletter Subscriber",
        email: newsletter.email,
        message: "Newsletter subscription",
        source: "Newsletter",
        subscribe: newsletter.subscribe,
        website: newsletter.website,
      });
      toast.success("Thank you for subscribing!");
      setNewsletter({ email: "", subscribe: false, website: "" });
    } catch (err) {
      toast.error("Subscription failed. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="relative mt-12" data-testid="footer">
      {/* Top accent line */}
      <div
        aria-hidden="true"
        className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[color:var(--brand-primary)]/40 to-transparent"
      />

      {/* CTA banner */}
      <div className="container-page pb-12 pt-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative liquid-glass aurora p-8 sm:p-12 overflow-hidden"
        >
          <div className="relative z-10 flex flex-col items-center justify-between gap-8 text-center lg:flex-row lg:items-center lg:text-left">
            <div className="max-w-xl">
              <span className="eyebrow mb-4 inline-flex"><span className="dot" /> Let's build</span>
              <h3 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl mt-3 leading-tight">
                Ready to ship AI that <span className="gradient-text">moves the needle?</span>
              </h3>
              <p className="text-[var(--text-secondary)] mt-3 max-w-md">
                Book a 30-minute discovery call. We'll map your goals to a measurable AI roadmap.
              </p>
            </div>
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:justify-center lg:w-auto">
              <Button asChild className="btn-primary h-11 rounded-full px-6">
                <Link to="/contact" className="inline-flex items-center gap-2">
                  Book a discovery call
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="btn-outline h-11 rounded-full px-6">
                <Link to="/case-studies">See case studies</Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Main footer */}
      <div className="border-t border-[color:var(--border-subtle)] bg-[color:var(--surface-3)]/40">
        <div className="container-page py-14">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-12 lg:gap-8">
            {/* Logo + contact */}
            <div className="md:col-span-2 lg:col-span-4">
              <Link to="/" className="inline-flex items-center mb-5">
                <img
                  src={LOGO_SRC}
                  alt="Namespace Consultants"
                  className="h-20 sm:h-24 w-auto object-contain"
                />
              </Link>
              <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-6 max-w-sm">
                AI-accelerated digital engineering for mobile, web, and Salesforce. Production-grade outcomes for ambitious teams.
              </p>

              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-3">
                  <Phone className="w-4 h-4 text-[var(--brand-primary-light)] mt-0.5 flex-shrink-0" />
                  <a href={`tel:${siteContact.phone}`} className="text-[var(--text-primary)] hover:text-[var(--brand-primary-light)] transition-colors">
                    {siteContact.phone}
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <Mail className="w-4 h-4 text-[var(--brand-primary-light)] mt-0.5 flex-shrink-0" />
                  <a href={`mailto:${siteContact.email}`} className="text-[var(--text-primary)] hover:text-[var(--brand-primary-light)] transition-colors break-all">
                    {siteContact.email}
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-[var(--brand-primary-light)] mt-0.5 flex-shrink-0" />
                  <span className="text-[var(--text-secondary)] leading-relaxed">
                    {siteContact.address.line1}<br />{siteContact.address.line2}
                  </span>
                </li>
              </ul>
            </div>

            {/* Nav columns */}
            <div className="md:col-span-1 lg:col-span-4">
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
                {Object.entries(FOOTER_NAV).map(([heading, links]) => (
                  <div key={heading}>
                    <h4 className="font-semibold text-sm mb-5 text-[var(--text-primary)] tracking-wide uppercase">
                      {heading}
                    </h4>
                    <ul className="space-y-3 text-sm">
                      {links.map((link) =>
                        link.href ? (
                          <li key={link.label}>
                            <a
                              href={link.href}
                              download={link.download}
                              className="text-[var(--text-secondary)] hover:text-[var(--brand-primary-light)] transition-colors"
                            >
                              {link.label}
                            </a>
                          </li>
                        ) : (
                          <li key={link.label}>
                            <Link
                              to={link.to}
                              className="text-[var(--text-secondary)] hover:text-[var(--brand-primary-light)] transition-colors"
                            >
                              {link.label}
                            </Link>
                          </li>
                        ),
                      )}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Newsletter */}
            <div className="md:col-span-1 lg:col-span-4">
              <h4 className="font-semibold text-sm mb-2 text-[var(--text-primary)] tracking-wide uppercase">
                Stay in the loop
              </h4>
              <p className="text-sm text-[var(--text-secondary)] mb-5">
                Receive AI insights, reports, and product updates. No spam.
              </p>

              <form onSubmit={handleNewsletterSubmit} className="space-y-3">
                <div className="relative">
                  <Input
                    type="email"
                    placeholder="you@company.com"
                    value={newsletter.email}
                    onChange={(e) => setNewsletter({ ...newsletter, email: e.target.value })}
                    required
                    className="bg-[color:var(--input-bg)] border-[color:var(--border-default)] focus:border-[color:var(--brand-primary)] h-11 pr-12 rounded-full pl-5"
                    aria-label="Email"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    aria-label="Subscribe"
                    className="absolute right-1 top-1 bottom-1 px-4 rounded-full bg-gradient-to-br from-[color:var(--brand-primary)] to-[color:var(--brand-primary-dark)] text-white text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60 transition-transform hover:scale-[1.02]"
                  >
                    {loading ? "..." : <ArrowRight className="w-4 h-4" />}
                  </button>
                </div>

                {/* Honeypot */}
                <div className="absolute left-[-10000px] top-auto w-1 h-1 overflow-hidden" aria-hidden="true">
                  <label className="text-xs text-gray-400" htmlFor="newsletter-website">Website</label>
                  <Input
                    id="newsletter-website"
                    name="website"
                    value={newsletter.website}
                    onChange={(e) => setNewsletter({ ...newsletter, website: e.target.value })}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                <label className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
                  <input
                    type="checkbox"
                    checked={newsletter.subscribe}
                    onChange={(e) => setNewsletter({ ...newsletter, subscribe: e.target.checked })}
                    className="rounded border-gray-600 w-3.5 h-3.5"
                  />
                  Also send product updates
                </label>
              </form>

              <div className="mt-6 inline-flex items-center gap-2 text-xs text-[var(--text-muted)]">
                <Sparkles className="w-3.5 h-3.5 text-[var(--brand-accent-cyan)]" />
                Trusted by 50+ enterprise teams
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom strip */}
      <div className="border-t border-[color:var(--border-subtle)]">
        <div className="container-page flex flex-col items-center justify-between gap-4 py-6 text-center lg:flex-row lg:text-left">
          <p className="text-xs text-[var(--text-muted)]">
            © {new Date().getFullYear()} NameSpace Consultants. All rights reserved.
          </p>

          <div className="flex items-center gap-2">
            {SOCIAL.map(({ label, href, Icon }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={label}
                className="w-9 h-9 rounded-full inline-flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] border border-[color:var(--border-subtle)] hover:border-[color:var(--border-brand)] hover:bg-[color:var(--brand-primary)]/10 transition-all"
              >
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>

          <div className="inline-flex items-center gap-2 text-xs text-[var(--text-muted)]">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            All systems operational
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
